<script setup>
import { computed, nextTick, ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import FocusSpotlight from 'dashboard/components-next/sidebar/FocusSpotlight.vue';
import {
  useFocusSpotlight,
  findSpotTarget,
} from 'dashboard/composables/useFocusSpotlight';
import { prefersReducedMotion } from 'dashboard/composables/useFocusMotion';
import { useGuidedTour } from 'dashboard/composables/useGuidedTour';
import { useSmallScreen } from 'dashboard/composables/useSmallScreen';
import BrasaMascot from './BrasaMascot.vue';
import { bearingBetween } from './brasaStates';

const MASCOT_SIZE = 52;
// Margem minima que separa o mascote e o cartao das bordas da tela.
const EDGE_MARGIN = 8;
const CARD_MARGIN = 16;
const INVITE_WAVE_MS = 2200;
const FLY_MS = 900;
const LAND_MS = 900;
const SLEEP_LINGER_MS = 1200;
const CELEBRATE_MS = 1400;
const NUDGE_MS = 700;
const SLEEP_FADE_MS = 400;
const EXIT_FLY_MS = 900;
const DEMO_DELAY_MS = 1400;
const DEMO_LEG_MS = 650;
const DEMO_REPEAT_MS = 7000;

const { t } = useI18n();
const { isSmallScreen } = useSmallScreen();
const {
  currentTour,
  currentStep,
  stepIndex,
  stepCount,
  inviteOpen,
  running,
  floating,
  activeSelector,
  nudged,
  start,
  next,
  prev,
  pause,
  skip,
  declineInvite,
  advanceFromEvent,
} = useGuidedTour();
const { spotlightStyle, spotlightRect, setSelector, measure, attach, detach } =
  useFocusSpotlight();

const cardRef = ref(null);
const confirmingSkip = ref(false);
const inviteState = ref('wave');
const mascotState = ref('idle');
const mascotLook = ref(null);
const mascotAim = ref(null);
const mascotFlip = ref(false);
const mascotLinger = ref(false);
const mascotExiting = ref(false);
const mascotTakingOff = ref(false);
const mascotFading = ref(false);
const mascotDemoing = ref(false);
const mascotStyle = ref(null);
// Olhar e facho so fazem sentido pousado apontando (e no nudge, que e um
// sacudir em cima do point). No voo, no sono e na comemoracao o rumo zera.
const mascotLookProp = computed(() =>
  ['point', 'nudge'].includes(mascotState.value) ? mascotLook.value : null
);
const mascotAimProp = computed(() =>
  mascotState.value === 'point' ? mascotAim.value : null
);
// Espelhado so enquanto voa pra esquerda; pousado, o rig volta ao normal
// senao o facho e os olhos apontam pro lado contrario do alvo.
const mascotFlipProp = computed(
  () => mascotState.value === 'fly' && mascotFlip.value
);
let previousFocus = null;
let previousPerchX = null;
let lastTargetKey = null;
let onDocKeydown = null;
let onAdvance = null;
let inviteTimer = null;
let stepAnimToken = 0;
const stepTimers = [];
const exitTimers = [];

const isInvite = computed(() => inviteOpen.value);
const isRunning = computed(() => running.value && Boolean(currentStep.value));
const isOpen = computed(() => isInvite.value || isRunning.value);
const hideSpotlight = computed(
  () => isSmallScreen.value || floating.value || !activeSelector.value
);
const isSheet = computed(() => isSmallScreen.value || hideSpotlight.value);

const title = computed(() => {
  if (isInvite.value && currentTour.value) {
    return t('ONBOARDING.TOURS.INVITE_TITLE');
  }
  const step = currentStep.value;
  if (!step) return '';
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(step.titleKey);
});

const body = computed(() => {
  if (isInvite.value && currentTour.value) {
    return t('ONBOARDING.TOURS.INVITE_BODY', { count: stepCount.value });
  }
  const step = currentStep.value;
  if (!step) return '';
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(step.bodyKey);
});

const actionHint = computed(() => {
  const step = currentStep.value;
  if (!isRunning.value || !step?.actionHintKey) return '';
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(step.actionHintKey);
});

const missingAnchor = computed(() => {
  return isRunning.value && hideSpotlight.value && !isSmallScreen.value;
});

const cardMascotState = computed(() => {
  if (!isRunning.value) return null;
  if (isSmallScreen.value) return 'idle';
  if (floating.value || missingAnchor.value) return 'hover';
  return null;
});

const showMascotLayer = computed(() => {
  if (mascotLinger.value && mascotStyle.value) return true;
  if (mascotTakingOff.value && mascotStyle.value) return true;
  return (
    isRunning.value &&
    !hideSpotlight.value &&
    Boolean(spotlightRect.value) &&
    Boolean(mascotStyle.value)
  );
});

// Viagem longa so durante o voo; fora dele a camada apenas acompanha o
// retangulo (scroll, resize) sem parecer que decolou de novo.
const mascotLayerClass = computed(() => {
  if (mascotExiting.value) {
    return '[transition:top_900ms_cubic-bezier(.45,.05,.2,1),left_900ms_cubic-bezier(.45,.05,.2,1),opacity_400ms_ease-out_400ms] motion-reduce:transition-none';
  }
  if (mascotFading.value) {
    return 'transition-opacity duration-[400ms] ease-out motion-reduce:transition-none';
  }
  if (mascotDemoing.value) {
    return 'transition-[top,left] duration-[650ms] ease-in-out motion-reduce:transition-none';
  }
  if (mascotState.value === 'fly') {
    return 'transition-[top,left] duration-[900ms] ease-[cubic-bezier(.45,.05,.2,1)] motion-reduce:transition-none';
  }
  return 'transition-[top,left] duration-200 ease-out motion-reduce:transition-none';
});

// Chave do alvo do passo: so quando ela muda o Brasa voa de novo. Remedicao
// do mesmo retangulo (scroll, resize, observer) apenas move a camada.
const targetKey = computed(() => `${stepIndex.value}:${activeSelector.value}`);

const counterLabel = computed(() =>
  t('ONBOARDING.TOURS.COUNTER', {
    current: stepIndex.value + 1,
    total: stepCount.value,
  })
);

const cardStyle = computed(() => {
  if (isSmallScreen.value || isInvite.value) return null;
  if (hideSpotlight.value || !spotlightRect.value) return null;
  const box = spotlightRect.value;
  const cardWidth = 320;
  const gap = 12;
  const viewportWidth =
    typeof window !== 'undefined' ? window.innerWidth : 1280;
  const viewportHeight =
    typeof window !== 'undefined' ? window.innerHeight : 800;
  // Alvo que comeca fora da tela (a pagina inteira mede left: -8) trazia o
  // cartao junto e cortava a borda esquerda: a margem vale nos dois lados.
  // Em tela estreita o Math.max do clamp da direita segura a margem, entao
  // left nunca fica negativo.
  let left = Math.max(CARD_MARGIN, box.left);
  if (left + cardWidth > viewportWidth - CARD_MARGIN) {
    left = Math.max(CARD_MARGIN, viewportWidth - cardWidth - CARD_MARGIN);
  }
  // Mesmo buraco em cima: alvo que comeca acima da dobra deixava o cartao
  // com top negativo, porque so existia o desvio de quando nao cabe embaixo.
  let top = Math.max(CARD_MARGIN, box.bottom + gap);
  if (top + 220 > viewportHeight - CARD_MARGIN) {
    top = Math.max(CARD_MARGIN, box.top - gap - 220);
  }
  return {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    right: 'auto',
    bottom: 'auto',
  };
});

const clearInviteTimer = () => {
  if (inviteTimer !== null) {
    window.clearTimeout(inviteTimer);
    inviteTimer = null;
  }
};

const clearStepTimers = () => {
  while (stepTimers.length) {
    window.clearTimeout(stepTimers.pop());
  }
};

const clearExitTimers = () => {
  while (exitTimers.length) {
    window.clearTimeout(exitTimers.pop());
  }
};

const scheduleStep = (fn, ms) => {
  const id = window.setTimeout(() => {
    const idx = stepTimers.indexOf(id);
    if (idx >= 0) stepTimers.splice(idx, 1);
    fn();
  }, ms);
  stepTimers.push(id);
  return id;
};

const scheduleExit = (fn, ms) => {
  const id = window.setTimeout(() => {
    const idx = exitTimers.indexOf(id);
    if (idx >= 0) exitTimers.splice(idx, 1);
    fn();
  }, ms);
  exitTimers.push(id);
  return id;
};

// Demonstracao do passo (ex.: arrastar a barra): depois de pousar e apontar,
// o Brasa vai ate o elemento, faz o gesto duas vezes e volta pro pouso.
// Repete de tempos em tempos enquanto o passo estiver aberto.
const demoOf = () => currentStep.value?.demo || null;
let perchStyle = null;

const runDemo = token => {
  const demo = demoOf();
  const box = spotlightRect.value;
  if (!demo || !box || !perchStyle || prefersReducedMotion()) return;
  if (token !== stepAnimToken) return;
  const M = MASCOT_SIZE;
  const dist = demo.distance || 56;
  const gripX = box.left + box.width / 2 - M / 2;
  const gripY = box.top + box.height / 2 - M / 2;
  const vertical = demo.kind === 'drag-y';
  const horizontal = demo.kind === 'drag-x';
  // Vai, volta, vai e volta: o gesto so le como gesto quando repete.
  const away = vertical
    ? { top: gripY + dist, left: gripX }
    : { top: gripY, left: gripX - dist };
  const home = { top: gripY, left: gripX };
  const legs = vertical || horizontal ? [home, away, home, away, home] : [];
  if (!legs.length) return;
  mascotDemoing.value = true;
  mascotState.value = 'hover';
  const applyLeg = (leg, i) => {
    mascotStyle.value = {
      top: `${leg.top}px`,
      left: `${leg.left}px`,
      opacity: '1',
    };
    // Rumo do gesto: 0 e pra cima e cresce no sentido horario.
    if (vertical) mascotLook.value = i % 2 === 1 ? 180 : 0;
    else mascotLook.value = i % 2 === 1 ? 270 : 90;
  };
  legs.forEach((leg, i) => {
    if (i === 0) {
      applyLeg(leg, i);
      return;
    }
    scheduleStep(() => {
      if (token !== stepAnimToken) return;
      applyLeg(leg, i);
    }, i * DEMO_LEG_MS);
  });
  scheduleStep(() => {
    if (token !== stepAnimToken) return;
    mascotStyle.value = { ...perchStyle };
    mascotState.value = 'fly';
  }, legs.length * DEMO_LEG_MS);
  scheduleStep(
    () => {
      if (token !== stepAnimToken) return;
      mascotDemoing.value = false;
      mascotState.value = 'point';
      const c = {
        x: parseFloat(perchStyle.left) + M / 2,
        y: parseFloat(perchStyle.top) + M / 2,
      };
      const bearing = bearingBetween(c, {
        x: box.left + box.width / 2,
        y: box.top + box.height / 2,
      });
      mascotLook.value = bearing;
      mascotAim.value = bearing;
    },
    legs.length * DEMO_LEG_MS + DEMO_LEG_MS
  );
  scheduleStep(
    () => runDemo(token),
    legs.length * DEMO_LEG_MS + DEMO_LEG_MS + DEMO_REPEAT_MS
  );
};

const scheduleDemo = token => {
  if (!demoOf()) return;
  scheduleStep(() => runDemo(token), DEMO_DELAY_MS);
};

// Prende num intervalo. Quando o intervalo e invalido (tela menor que o
// mascote) o minimo ganha: melhor encostado na borda de cima/esquerda do que
// jogado pra fora dela.
const clampTo = (value, min, max) => Math.max(min, Math.min(value, max));

const computePerch = box => {
  const M = MASCOT_SIZE;
  const innerWidth = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const innerHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
  const midY = box.top + box.height / 2 - M / 2;
  // perch.x e o CENTRO do mascote, perch.y e o topo dele (ver applyPerch).
  let perch = { x: box.right - 14, y: box.top - M * 0.78 };
  if (perch.y < EDGE_MARGIN) {
    perch = { x: box.right + 6 + M / 2, y: midY };
  }
  if (perch.x + M / 2 > innerWidth - EDGE_MARGIN) {
    perch = { x: box.left - 6 - M / 2, y: midY };
  }
  // Alvo do tamanho da pagina nao tem lado de fora que caiba, e o desvio pra
  // esquerda mandava o Brasa pra left: -66px (tour de Tarefas, staging). Sem
  // lado de fora, o poleiro vai pra DENTRO da caixa, no mesmo canto superior
  // direito onde ele pousa no caso normal.
  if (perch.x - M / 2 < EDGE_MARGIN) {
    perch = { x: box.right - EDGE_MARGIN - M / 2, y: box.top + EDGE_MARGIN };
  }
  // Rede de seguranca nos quatro lados: nenhum retangulo (nem alvo estreito
  // colado na borda, nem alvo que comeca fora da tela) empurra o mascote pra
  // fora da area visivel.
  perch.x = clampTo(
    perch.x,
    EDGE_MARGIN + M / 2,
    innerWidth - EDGE_MARGIN - M / 2
  );
  perch.y = clampTo(perch.y, EDGE_MARGIN, innerHeight - EDGE_MARGIN - M);
  return perch;
};

const applyPerch = (box, fresh = true) => {
  const perch = computePerch(box);
  const M = MASCOT_SIZE;
  if (fresh) {
    mascotFlip.value = previousPerchX !== null && perch.x < previousPerchX - 10;
  }
  previousPerchX = perch.x;
  mascotTakingOff.value = false;
  mascotStyle.value = {
    top: `${perch.y}px`,
    left: `${perch.x - M / 2}px`,
    opacity: '1',
  };
  perchStyle = { ...mascotStyle.value };
  const mascotCenter = { x: perch.x, y: perch.y + M / 2 };
  const boxCenter = {
    x: box.left + box.width / 2,
    y: box.top + box.height / 2,
  };
  const bearing = bearingBetween(mascotCenter, boxCenter);
  mascotLook.value = bearing;
  mascotAim.value = bearing;
};

const startFlyLandPoint = () => {
  stepAnimToken += 1;
  const token = stepAnimToken;
  clearStepTimers();
  if (prefersReducedMotion()) {
    mascotState.value = 'point';
    return;
  }
  mascotState.value = 'fly';
  scheduleStep(() => {
    if (token !== stepAnimToken) return;
    mascotState.value = 'land';
    scheduleStep(() => {
      if (token !== stepAnimToken) return;
      mascotState.value = 'point';
      scheduleDemo(token);
    }, LAND_MS);
  }, FLY_MS);
};

const beginSleepExit = action => {
  const visible = showMascotLayer.value;
  stepAnimToken += 1;
  clearStepTimers();
  mascotDemoing.value = false;
  mascotState.value = 'sleep';
  mascotExiting.value = false;
  mascotFading.value = false;
  mascotTakingOff.value = false;
  clearExitTimers();
  if (!visible) {
    action();
    return;
  }
  mascotLinger.value = true;
  scheduleExit(() => {
    mascotFading.value = true;
    if (mascotStyle.value) {
      mascotStyle.value = { ...mascotStyle.value, opacity: '0' };
    }
    scheduleExit(() => {
      mascotLinger.value = false;
      mascotFading.value = false;
    }, SLEEP_FADE_MS);
  }, SLEEP_LINGER_MS);
  action();
};

// Fim do tour: comemora curto, sobe e some. Usado pelo botao e pelo avanco
// por acao no proprio alvo (advanceFromEvent) no ultimo passo.
const beginCelebrateExit = () => {
  const visible = showMascotLayer.value;
  stepAnimToken += 1;
  clearStepTimers();
  mascotDemoing.value = false;
  mascotState.value = 'celebrate';
  mascotExiting.value = false;
  mascotFading.value = false;
  mascotTakingOff.value = false;
  clearExitTimers();
  if (!visible) return;
  mascotLinger.value = true;
  scheduleExit(() => {
    mascotExiting.value = true;
    mascotState.value = 'fly';
    if (mascotStyle.value) {
      const left = parseFloat(mascotStyle.value.left) || 0;
      mascotStyle.value = {
        ...mascotStyle.value,
        top: `-${MASCOT_SIZE + 40}px`,
        left: `${left + 40}px`,
        opacity: '0',
      };
    }
    scheduleExit(() => {
      mascotLinger.value = false;
      mascotExiting.value = false;
    }, EXIT_FLY_MS);
  }, CELEBRATE_MS);
};

const restoreFocus = () => {
  if (previousFocus && typeof previousFocus.focus === 'function') {
    previousFocus.focus();
  }
  previousFocus = null;
};

const focusCard = () => {
  nextTick(() => {
    const root = cardRef.value;
    if (!root) return;
    const preferred =
      root.querySelector('[data-testid="guided-tour-start"]') ||
      root.querySelector('[data-testid="guided-tour-next"]') ||
      root.querySelector('button');
    preferred?.focus();
  });
};

const clearSpotlight = () => {
  detach();
  setSelector('');
};

const syncSpotlight = selector => {
  if (!selector || hideSpotlight.value) {
    clearSpotlight();
    return;
  }
  attach([selector]);
  setSelector(selector);
  measure();
};

const actionButtons = () => {
  if (!cardRef.value) return [];
  return [...cardRef.value.querySelectorAll('button:not([disabled])')];
};

const trapTab = event => {
  if (event.key !== 'Tab' || !isOpen.value) return;
  const buttons = actionButtons();
  if (!buttons.length) return;
  const first = buttons[0];
  const last = buttons[buttons.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
    return;
  }
  if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};

onDocKeydown = event => {
  if (!isOpen.value) return;
  if (event.key === 'Tab') {
    trapTab(event);
    return;
  }
  if (event.key !== 'Escape') return;
  event.preventDefault();
  confirmingSkip.value = false;
  beginSleepExit(() => pause());
};

// Clique fora do cartao que nao avancou o passo: o Brasa chama atencao
// (sacode 700 ms) e volta a apontar. E o "precisa de atencao" da folha.
const triggerNudge = () => {
  if (mascotState.value !== 'point' || mascotDemoing.value) return;
  mascotState.value = 'nudge';
  scheduleStep(() => {
    if (mascotState.value === 'nudge') mascotState.value = 'point';
  }, NUDGE_MS);
};

onAdvance = event => {
  if (!isRunning.value) return;
  const wasLast = stepIndex.value === stepCount.value - 1;
  const advanced = advanceFromEvent(event);
  if (advanced && wasLast) {
    beginCelebrateExit();
    return;
  }
  if (advanced || event.type !== 'click') return;
  if (cardRef.value?.contains(event.target)) return;
  triggerNudge();
};

const attachDoc = () => {
  if (typeof document === 'undefined') return;
  document.addEventListener('keydown', onDocKeydown);
  document.addEventListener('dblclick', onAdvance, true);
  document.addEventListener('click', onAdvance, true);
};

const detachDoc = () => {
  if (typeof document === 'undefined') return;
  document.removeEventListener('keydown', onDocKeydown);
  document.removeEventListener('dblclick', onAdvance, true);
  document.removeEventListener('click', onAdvance, true);
};

const onSkipClick = () => {
  confirmingSkip.value = true;
};

const onSkipCancel = () => {
  confirmingSkip.value = false;
};

const onSkipConfirm = () => {
  beginSleepExit(() => {
    confirmingSkip.value = false;
    skip();
  });
};

// "Agora nao" fecha na hora: a pessoa disse nao e o cartao some, sem o
// Brasa segurar a tela pra adormecer na frente dela.
const onDeclineInvite = () => {
  clearInviteTimer();
  declineInvite();
};

// Decola do cartao do convite: a camada nasce onde o cartao esta e a troca de
// posicao no primeiro pouso vira a viagem ate o alvo.
const onStartClick = () => {
  const box = cardRef.value?.getBoundingClientRect?.();
  if (box && !isSmallScreen.value && !prefersReducedMotion()) {
    mascotStyle.value = {
      top: `${box.top + 4}px`,
      left: `${box.left + 8}px`,
      opacity: '1',
    };
    previousPerchX = box.left + 8 + MASCOT_SIZE / 2;
    mascotState.value = 'fly';
    mascotTakingOff.value = true;
  }
  start();
};

const onNextClick = () => {
  if (stepIndex.value === stepCount.value - 1) {
    beginCelebrateExit();
  }
  next();
};

watch(
  [isInvite, () => currentTour.value?.key],
  ([open]) => {
    clearInviteTimer();
    if (!open) return;
    inviteState.value = 'wave';
    inviteTimer = window.setTimeout(() => {
      inviteState.value = 'idle';
      inviteTimer = null;
    }, INVITE_WAVE_MS);
  },
  { immediate: true }
);

const forgetPerch = () => {
  mascotStyle.value = null;
  perchStyle = null;
  previousPerchX = null;
  lastTargetKey = null;
};

watch(
  [isRunning, targetKey, spotlightRect, hideSpotlight],
  () => {
    if (!isRunning.value) {
      lastTargetKey = null;
      mascotTakingOff.value = false;
      if (!mascotLinger.value) forgetPerch();
      return;
    }
    if (mascotLinger.value) return;
    if (hideSpotlight.value || !spotlightRect.value) {
      // Sem ancora (ou celular): a camada nao tem onde pousar; quem aparece
      // e o Brasa pequeno do cartao. Decolagem pendente e cancelada.
      mascotTakingOff.value = false;
      return;
    }
    const fresh = targetKey.value !== lastTargetKey || !mascotStyle.value;
    lastTargetKey = targetKey.value;
    applyPerch(spotlightRect.value, fresh);
    if (fresh) startFlyLandPoint();
  },
  { immediate: true }
);

// Acabou o linger (sleep/celebrate) com o tour ja rodando de novo (pausou e
// retomou rapido): voa pro alvo atual, senao ficaria parado sem camada.
watch(mascotLinger, lingering => {
  if (lingering) return;
  if (!isRunning.value) {
    forgetPerch();
    return;
  }
  if (hideSpotlight.value || !spotlightRect.value) return;
  lastTargetKey = targetKey.value;
  applyPerch(spotlightRect.value, true);
  startFlyLandPoint();
});

watch(confirmingSkip, value => {
  if (value) {
    stepAnimToken += 1;
    clearStepTimers();
    mascotState.value = 'wait';
    return;
  }
  if (mascotState.value === 'wait') {
    mascotState.value = 'point';
  }
});

watch(
  isOpen,
  open => {
    if (open) {
      previousFocus = previousFocus || document.activeElement;
      attachDoc();
      confirmingSkip.value = false;
      if (!hideSpotlight.value && activeSelector.value) {
        syncSpotlight(activeSelector.value);
      } else {
        clearSpotlight();
      }
      focusCard();
      return;
    }
    confirmingSkip.value = false;
    clearSpotlight();
    detachDoc();
    restoreFocus();
  },
  { immediate: true }
);

watch([activeSelector, hideSpotlight], ([selector, hidden]) => {
  if (!isOpen.value || hidden || !selector) {
    if (hidden) clearSpotlight();
    return;
  }
  if (!findSpotTarget(selector)) {
    clearSpotlight();
    return;
  }
  syncSpotlight(selector);
});

onBeforeUnmount(() => {
  clearInviteTimer();
  clearStepTimers();
  clearExitTimers();
  detachDoc();
  clearSpotlight();
});
</script>

<template>
  <TeleportWithDirection to="body">
    <template v-if="isOpen">
      <FocusSpotlight v-if="!hideSpotlight" :style-value="spotlightStyle" />
      <div
        ref="cardRef"
        role="dialog"
        aria-modal="true"
        :aria-label="t('ONBOARDING.TOURS.ARIA_LABEL')"
        aria-labelledby="guided-tour-title"
        class="z-[80] w-80 max-w-[calc(100vw-2rem)] overflow-visible rounded-2xl border border-n-weak bg-n-solid-2 p-4 shadow-xl"
        :class="{
          'fixed inset-x-4 bottom-4 end-auto w-auto': isSheet && !cardStyle,
          'fixed bottom-4 end-4': !isSheet && !cardStyle,
          'ring-2 ring-n-iris-9': nudged,
        }"
        :style="cardStyle"
        :data-testid="isInvite ? 'guided-tour-invite' : 'guided-tour'"
        @click.stop
      >
        <p
          v-if="isRunning"
          class="m-0 mb-2 text-[11px] font-semibold tabular-nums text-n-iris-11"
          data-testid="guided-tour-step"
          aria-live="polite"
        >
          {{ counterLabel }}
        </p>
        <div class="mb-1 flex items-start gap-2">
          <BrasaMascot
            v-if="isInvite"
            class="-mt-0.5 -ms-1 shrink-0"
            :size="40"
            :state="inviteState"
          />
          <BrasaMascot
            v-else-if="cardMascotState"
            class="-mt-0.5 -ms-1 shrink-0"
            :size="40"
            :state="cardMascotState"
            :look="cardMascotState === 'hover' ? 90 : null"
          />
          <p
            id="guided-tour-title"
            class="m-0 text-sm font-semibold text-n-slate-12"
          >
            {{ title }}
          </p>
        </div>
        <p class="m-0 mb-3 text-sm leading-relaxed text-n-slate-11">
          {{ body }}
        </p>
        <p
          v-if="actionHint"
          class="m-0 mb-3 text-xs font-medium text-n-iris-11"
          data-testid="guided-tour-hint"
        >
          {{ actionHint }}
        </p>
        <p
          v-else-if="missingAnchor"
          class="m-0 mb-3 text-xs text-n-slate-10"
          data-testid="guided-tour-no-anchor"
        >
          {{ t('ONBOARDING.TOURS.NO_ANCHOR') }}
        </p>

        <div v-if="isInvite" class="flex flex-col items-stretch gap-2">
          <button
            type="button"
            class="min-h-11 w-full rounded-full bg-n-slate-12 px-4 py-2 text-sm font-semibold leading-snug text-n-background hover:opacity-90"
            data-testid="guided-tour-start"
            @click="onStartClick"
          >
            {{ t('ONBOARDING.TOURS.START') }}
          </button>
          <button
            type="button"
            class="min-h-11 w-full rounded-full px-3 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
            data-testid="guided-tour-later"
            @click="onDeclineInvite"
          >
            {{ t('ONBOARDING.TOURS.LATER') }}
          </button>
        </div>

        <div
          v-else-if="confirmingSkip"
          class="flex flex-col items-stretch gap-2"
        >
          <p class="m-0 text-sm text-n-slate-12">
            {{ t('ONBOARDING.TOURS.SKIP_CONFIRM') }}
          </p>
          <button
            type="button"
            class="min-h-11 w-full rounded-full bg-n-slate-12 px-4 py-2 text-sm font-semibold text-n-background hover:opacity-90"
            data-testid="guided-tour-skip-yes"
            @click="onSkipConfirm"
          >
            {{ t('ONBOARDING.TOURS.SKIP_YES') }}
          </button>
          <button
            type="button"
            class="min-h-11 w-full rounded-full px-3 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
            data-testid="guided-tour-skip-no"
            @click="onSkipCancel"
          >
            {{ t('ONBOARDING.TOURS.SKIP_NO') }}
          </button>
        </div>

        <div v-else class="flex flex-wrap items-stretch gap-2">
          <button
            type="button"
            class="min-h-11 min-w-11 flex-1 rounded-full px-3 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40"
            data-testid="guided-tour-prev"
            :disabled="stepIndex <= 0"
            @click="prev()"
          >
            {{ t('ONBOARDING.TOURS.PREV') }}
          </button>
          <button
            type="button"
            class="min-h-11 min-w-11 flex-1 rounded-full bg-n-slate-12 px-4 py-2 text-sm font-semibold text-n-background hover:opacity-90"
            data-testid="guided-tour-next"
            @click="onNextClick"
          >
            {{ t('ONBOARDING.TOURS.NEXT') }}
          </button>
          <button
            type="button"
            class="min-h-11 w-full rounded-full px-3 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
            data-testid="guided-tour-skip"
            @click="onSkipClick"
          >
            {{ t('ONBOARDING.TOURS.SKIP') }}
          </button>
        </div>
      </div>
    </template>
    <div
      v-if="showMascotLayer"
      class="pointer-events-none fixed z-[80]"
      :class="mascotLayerClass"
      :style="mascotStyle"
      data-testid="guided-tour-mascot"
      aria-hidden="true"
    >
      <BrasaMascot
        :size="MASCOT_SIZE"
        :state="mascotState"
        :look="mascotLookProp"
        :aim="mascotAimProp"
        :flip="mascotFlipProp"
      />
    </div>
  </TeleportWithDirection>
</template>
